/**
 * First-touch landing page (WIN-1629): the path a visitor first arrived on, kept next to
 * the ad attribution so leads and tickets can be grouped per landing page. Never
 * overwritten while it is within the same 90 day window as the ad click.
 */
import * as LocalStorage from '~/utilities/localStorage'
import { MAX_ATTRIBUTION_AGE_MS } from '~/utilities/adAttribution'

export const LANDING_STORAGE_KEY = 'landingPage'

const LOCALE_PREFIX = /^\/(en|es)(?=\/|$)/
const LOOKUP_PATH = /^\/(traffic\/lookup|lookup|mysummons)(\/|$)/

/** 'lookup' for the web Lookup funnel, 'other' for anything else. */
export const landingCategoryFor = (path) => {
  if (typeof path !== 'string' || !path) return undefined
  const normalized = path.split(/[?#]/)[0].toLowerCase().replace(LOCALE_PREFIX, '') || '/'
  return LOOKUP_PATH.test(normalized) ? 'lookup' : 'other'
}

const readStored = () => {
  try {
    const stored = LocalStorage.get(LANDING_STORAGE_KEY, true)
    if (!stored || typeof stored !== 'object' || !stored.landingPage) return undefined
    const savedAt = Date.parse(stored.savedAt)
    if (!savedAt || Date.now() - savedAt > MAX_ATTRIBUTION_AGE_MS) {
      LocalStorage.remove(LANDING_STORAGE_KEY)
      return undefined
    }
    return stored
  } catch (error) {
    return undefined
  }
}

/** Record the current path as the landing page unless one is already stored. */
export const captureLandingPage = () => {
  if (typeof window === 'undefined' || !window.location) return undefined
  const stored = readStored()
  if (stored) return stored
  const landingPage = window.location.pathname
  if (!landingPage) return undefined
  const payload = { landingPage, savedAt: new Date().toISOString() }
  try {
    LocalStorage.set(LANDING_STORAGE_KEY, payload)
  } catch (error) {
    // Storage is full or blocked: nothing to send later.
  }
  return payload
}

/** `{ landingPage, landingCategory }` for request bodies; empty when unknown. */
export const getLanding = () => {
  const stored = readStored()
  if (!stored) return {}
  const landingCategory = landingCategoryFor(stored.landingPage)
  return {
    landingPage: stored.landingPage,
    ...(landingCategory ? { landingCategory } : {}),
  }
}
